import { memo, useState } from 'react';
import styles from '../styles/Header.module.css';
import clsx from '../utils/clsx';
import { deletePlayer, readSeatDiags } from '../multiplayer';
import copyText from '../utils/copyText';
import { visibleSections } from '../engine/settings/registry';
import { diag } from '../diagnostics/log';
import { formatBugReport } from '../diagnostics/report';
import { activatable, onActivateKey } from '../utils/keyActivation';
import PhasePlate from './PhasePlate';
import HeaderMenu from './header/HeaderMenu';
import menu from '../styles/HeaderMenu.module.css';

/** A flag that is true for 1.5 s after `flash()` - the "Copied!" on a copy button. */
function useFlash() {
  const [on, setOn] = useState(false);
  const flash = () => {
    setOn(true);
    setTimeout(() => setOn(false), 1500);
  };
  return [on, flash];
}

/** Copies `text()` and flashes on success; nothing when there is nothing to copy. */
async function copyAndFlash(text, flash) {
  if (text === null) return;
  if (await copyText(text)) flash();
}

/**
 * THE WHOLE TABLE (v8.362.0): every other seat's own uploaded lines,
 * offline seats included - the one that dropped is the one to read. The
 * lines are each person's `seatDiag` document, by uid (2026-10-31).
 */
function reportTable(connectedPlayers, me, lines) {
  return (connectedPlayers || [])
    .filter((p) => p.id !== me)
    .map((p) => ({
      name: p.displayName || p.id,
      role: p.isGm ? 'GM' : 'player',
      online: p.online !== false,
      diag: lines[p.uid]?.diag ?? '',
      diagAt: lines[p.uid]?.diagAt || null,
    }));
}

/**
 * Everything a bug report needs, in one copy: the build, the backend,
 * the session and role, and the last few hundred things the app saw -
 * the swallowed 429, the listener in backoff, the sign-in that expired.
 * The person who hit the bug is the only one who was there.
 */
async function bugReport({ session, sessionId, isGm, connectedPlayers }) {
  // Read ONCE, as the report is copied - nobody listens to them. The GM reads every seat's; a player's report is
  // its own lines, already below.
  const lines = sessionId && isGm ? await readSeatDiags(sessionId) : {};
  return formatBugReport({
    context: diag.context(),
    entries: diag.entries(),
    env: { userAgent: navigator.userAgent, url: window.location.href, now: Date.now() },
    table: reportTable(connectedPlayers, session?.playerId, lines),
  });
}

/** WHICH BUILD THIS IS. */
function Title() {
  return (
    <h1 className={styles.title}>
      DEATHWATCH COMBAT SIMULATOR
      {/* Small, permanent, and selectable: with a preview channel live
          beside the real site, "it works for me" is not answerable without it. */}
      <span className={styles.version} title="Build version" data-testid="app-version">
        v{__APP_VERSION__}
      </span>
    </h1>
  );
}

/** The hairline between the header's groups. */
const DIVIDER = <span className={styles.rule} aria-hidden="true" />;

/** The GM's session code (click to copy) and the invite link. */
function SessionShare({ session }) {
  const [codeCopied, flashCode] = useFlash();
  const [linkCopied, flashLink] = useFlash();
  // Not navigator.clipboard directly: that is undefined on a non-secure
  // origin, which is how this app is opened over the tailnet, and the
  // button then appears dead. See utils/copyText.
  const copyCode = () => copyAndFlash(session?.code ? session.code : null, flashCode);
  /** The whole handover, as one URL. */
  const copyLink = () =>
    copyAndFlash(
      session?.code ? `${window.location.origin}${window.location.pathname}?join=${session.code}` : null,
      flashLink,
    );
  return (
    <>
      <span className={styles.code} title="Click to copy session code" {...activatable(copyCode)}>
        Code <b>{codeCopied ? 'Copied!' : session.code}</b>
      </span>
      <button
        className={styles.word}
        title="Copy an invite link - the code is in the URL, so a player only types their name"
        data-testid="copy-invite-link"
        onClick={copyLink}
      >
        {linkCopied ? 'Copied!' : 'Invite'}
      </button>
    </>
  );
}

/** Who is here, with the GM's kick beside each player. */
function OnlinePlayers({ players, isGm, sessionId }) {
  return (
    <span className={styles.people} title={players.map((p) => p.displayName + (p.isGm ? ' (GM)' : '')).join(', ')}>
      {players.map((p) => (
        <span key={p.id}>
          {p.displayName || '?'}
          {isGm && !p.isGm && (
            <KickButton name={p.displayName} onKick={() => deletePlayer(sessionId, p.id).catch(console.error)} />
          )}
          {p !== players[players.length - 1] ? ', ' : ''}
        </span>
      ))}{' '}
      ({players.length})
    </span>
  );
}

/**
 * The kick ✕, named after its player: every one looks the same, so the
 * accessible name is the only thing that says whom it throws out - and
 * e2e/presence clicks it by that name, in one lookup.
 */
function KickButton({ name, onKick }) {
  return (
    <span
      className={styles.kick}
      title="Kick player"
      aria-label={`Kick ${name || '?'}`}
      role="button"
      tabIndex={0}
      onClick={(ev) => {
        ev.stopPropagation();
        onKick();
      }}
      onKeyDown={onActivateKey(onKick)}
    >
      ✕
    </span>
  );
}

/**
 * One command of the more menu. `keep` leaves the menu open after the press
 * (the bug report says "copied" in place).
 */
function MenuItem({ onPick, close, keep = false, quiet = false, children, ...rest }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={clsx(menu.item, quiet && menu.quietItem)}
      onClick={() => {
        onPick();
        if (!keep) close();
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * THE MORE MENU (v9.6.0): what is not pressed in play - the file's tools,
 * the rules, who may act, the keys, and the bug report. Each keeps the name
 * and id it had on the bar. The hidden file input lives OUTSIDE the panel:
 * the panel unmounts when it closes, and the picker answers after that.
 */
function MoreMenu({ p }) {
  const [copied, flash] = useFlash();
  const canSettings = visibleSections(!!p.canEditSession).length > 0;
  return (
    <>
      <HeaderMenu
        label="⋯"
        className={clsx(styles.word, styles.quiet)}
        testId="more-menu"
        panelTestId="more-panel"
        ariaLabel="More: files, settings, keys"
        role="menu"
        align="right"
        title="More: export, import, settings, locks, hotkeys, bug report"
      >
        {(close) => (
          <>
            <MenuItem onPick={p.doExport} close={close} title="Export session as JSON">
              Export
            </MenuItem>
            <MenuItem onPick={() => p.fileInputRef.current?.click()} close={close} title="Import session from JSON">
              Import
            </MenuItem>
            {canSettings && (
              <MenuItem onPick={() => p.setShowSettings(true)} close={close} title="Settings">
                Settings
              </MenuItem>
            )}
            {p.sessionId && p.isGm && (
              <MenuItem onPick={() => p.setShowPermPanel(true)} close={close} title="Who may act">
                Locks
              </MenuItem>
            )}
            <MenuItem onPick={() => p.setShowHotkeys((h) => !h)} close={close}>
              F1 Hotkeys
            </MenuItem>
            <MenuItem
              onPick={async () =>
                copyAndFlash(await bugReport({ ...p, connectedPlayers: p.connectedPlayers ?? [] }), flash)
              }
              close={close}
              keep
              quiet
              title="Copy a bug report: build, backend, session and the recent events this browser saw"
              data-testid="bug-report-copy"
            >
              {copied ? 'report copied' : 'bug report'}
            </MenuItem>
          </>
        )}
      </HeaderMenu>
      <input
        ref={p.fileInputRef}
        type="file"
        accept=".json"
        data-testid="session-import-input"
        style={{ display: 'none' }}
        onChange={p.doImport}
      />
    </>
  );
}

/**
 * THE SESSION GROUP: the code and invite (GM), who I am, who is here, the
 * way out - and the more menu, which is there outside a session too.
 */
function SessionGroup({ p }) {
  const { sessionId, isGm, session, user, onlinePlayers } = p;
  const leave = p.onLeave || (() => p.setSession({ id: null, code: null, isGm: false, playerId: null }));
  return (
    <>
      {DIVIDER}
      <div className={styles.group}>
        {sessionId && isGm && <SessionShare session={session} />}
        {sessionId && (
          <span className={styles.me}>
            {user?.sessionName || user?.displayName} ({isGm ? 'GM' : 'Player'})
          </span>
        )}
        {sessionId && onlinePlayers.length > 0 && (
          <OnlinePlayers players={onlinePlayers} isGm={isGm} sessionId={sessionId} />
        )}
        {sessionId && (
          <button className={styles.word} onClick={leave}>
            Leave
          </button>
        )}
        <MoreMenu p={p} />
      </div>
    </>
  );
}

/** Says a control is closed by the GM's locks - in a word, where a padlock used to be. */
function LockMark() {
  return <span className={styles.lockMark}>locked</span>;
}

/** A panel toggle that lights when open and fades (and disables) when locked. */
function LibraryButton({ open, locked, onToggle }) {
  return (
    <button
      className={styles.word}
      data-testid="header-characters"
      data-on={open || undefined}
      onClick={() => !locked && onToggle((v) => !v)}
      disabled={locked}
    >
      Characters
      {locked ? <LockMark /> : null}
    </button>
  );
}

function TurnsButton({ active, locked, onToggle }) {
  return (
    <button
      className={styles.word}
      data-on={active || undefined}
      onClick={onToggle}
      disabled={locked}
      title="Toggle Turn Tracker"
    >
      Turns
      {locked ? <LockMark /> : null}
    </button>
  );
}

/** `unread` (a player's, v9.8.0): how many were shown to them and not opened; the count is then those. */
function HandoutsButton({ open, count, unread, onToggle }) {
  return (
    <button
      className={styles.word}
      data-on={open || undefined}
      onClick={onToggle}
      title={unread > 0 ? `Handouts - ${unread} you have not opened` : 'Handouts - what the GM shows the table'}
      aria-pressed={open}
      data-testid="handouts-toggle"
      data-unread={unread > 0 ? unread : undefined}
    >
      Handouts{count > 0 ? ` ${count}` : ''}
      {unread > 0 ? ' new' : ''}
    </button>
  );
}

/**
 * DISABLED WHEN THERE IS NOTHING TO UNDO, not merely faded. Availability
 * was expressed only as inline opacity, so the button stayed clickable
 * with an empty history - `doUndo` returned silently - and the state was
 * readable only by measuring a style. A disabled button says so to a
 * screen reader, to a pointer and to a test (v8.2.1). The lock case is
 * disabled too: `doUndo` already refuses it, and the padlock in the label
 * is what explains why.
 */
function UndoButton({ canUndo, saving, locked, onUndo }) {
  return (
    <button
      className={clsx(styles.word, styles.quiet)}
      disabled={!canUndo || locked}
      onClick={onUndo}
      // Off while the newest action's entry is written (2026-10-24): said, so it never reads as nothing to undo.
      title={saving ? 'Saving the last action - Undo in a moment' : 'Undo (Ctrl+Z)'}
    >
      Undo{locked ? <LockMark /> : null}
    </button>
  );
}

/**
 * The app's top bar, in words and FOUR GROUPS (v9.6.0; three since v8.424.0):
 *
 * - MODE: the three plates, the peek, the danger (PhasePlate);
 * - TABLE: one word opening sides, positions and Start the fight (PhasePlate
 *   draws it, beside the mode, for whoever directs);
 * - PLAY: Characters, Turns, Handouts, Undo - what is pressed in play;
 * - SESSION: the code, the people, Leave, and the more menu (files,
 *   settings, locks, hotkeys, the bug report).
 *
 * Props: the session (sessionId, isGm, session, user, onlinePlayers,
 * connectedPlayers), the panel toggles and their state, undo, export/import,
 * and the handouts drawer (absent outside a session).
 */
function Header(p) {
  const { isLocked, handoutsOpen = false, handoutCount = 0, handoutUnread = 0 } = p;
  return (
    <header className={styles.header}>
      <Title />
      {p.direction && (
        <PhasePlate
          direction={p.direction}
          canDirect={!p.sessionId || !!p.isGm}
          danger={p.danger}
          teams={p.teams}
          fightSides={p.fightSides}
          onStartFight={p.onStartFight}
        />
      )}
      {DIVIDER}
      {/* Play: the library hangs from Characters, and from here it opens over the centre column,
          not over a side panel. */}
      <div className={styles.group}>
        <div className={styles.anchor}>
          <LibraryButton
            open={p.showCharacterLibrary}
            locked={isLocked('characterLibrary')}
            onToggle={p.setShowCharacterLibrary}
          />
          {p.characterLibraryNode}
        </div>
        <TurnsButton active={p.turnTrackerActive} locked={p.turnTrackerLocked} onToggle={p.onToggleTurnTracker} />
        {p.onToggleHandouts && (
          <HandoutsButton
            open={handoutsOpen}
            count={handoutCount}
            unread={handoutUnread}
            onToggle={p.onToggleHandouts}
          />
        )}
        <UndoButton canUndo={p.canUndo} saving={p.undoSaving} locked={isLocked('undo')} onUndo={p.doUndo} />
        {p.assetsHref && (
          // A real link, in a new tab (v11.73.0): the library opens beside the table and never joins it.
          <a
            className={styles.word}
            href={p.assetsHref}
            target="_blank"
            rel="noopener noreferrer"
            title="Your asset library, in a new tab, with this session's copies"
            data-testid="open-assets-library"
          >
            Assets ↗
          </a>
        )}
      </div>
      <span className={styles.spring} />
      <SessionGroup p={p} />
    </header>
  );
}

export default memo(Header);
